import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon as BillingIcon } from '@enamelai/design-system/icons/Billing'
import { Icon as CampaignIcon } from '@enamelai/design-system/icons/Campaign'
import { Icon as ChartingIcon } from '@enamelai/design-system/icons/Charting'
import { Icon as ClaimsIcon } from '@enamelai/design-system/icons/Insurance_plus'
import { Icon as DashboardIcon } from '@enamelai/design-system/icons/Dashboard'
import { Icon as FilesIcon } from '@enamelai/design-system/icons/Files_letters'
import { Icon as FormBuilderIcon } from '@enamelai/design-system/icons/Form_builder'
import { Icon as ImageViewerIcon } from '@enamelai/design-system/icons/Image_viewer'
import { Icon as InsuranceIcon } from '@enamelai/design-system/icons/Insurance_module'
import { Icon as MembershipIcon } from '@enamelai/design-system/icons/Membership'
import { Icon as CommunicationsIcon } from '@enamelai/design-system/icons/Communications'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import { Icon as PatientsIcon } from '@enamelai/design-system/icons/Patient_info'
import { Icon as StaffIcon } from '@enamelai/design-system/icons/Staff'
import { Icon as ReportingIcon } from '@enamelai/design-system/icons/Reporting'
import { Icon as ScheduleIcon } from '@enamelai/design-system/icons/Scheduler'
import { Icon as SettingsIcon } from '@enamelai/design-system/icons/Settings'
import { Drawer, type DrawerItem } from '@enamelai/design-system'
import { StaffScope } from '@constants/Settings/staffPermission.const'
import { hasStaffScope } from '@utils/staffScope.util'
import { LeftSideBarWrapper } from './LeftSideBar.styled'

type NavRouteItem = {
  id: string
  label: string
  path: string
  icon: React.ReactNode
  match?: 'exact' | 'prefix'
  scope?: StaffScope
  hidden?: boolean
}

const NAV_ROUTES: NavRouteItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/home',
    icon: <DashboardIcon />,
    match: 'exact',
    hidden: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: <DashboardIcon />,
    match: 'exact',
    scope: StaffScope.DASHBOARD_READ,
  },
  {
    id: 'appointment',
    label: 'Appointment',
    path: '/appointment',
    icon: <ScheduleIcon />,
    match: 'exact',
    scope: StaffScope.SCHEDULING_READ,
  },
  {
    id: 'patients',
    label: 'Patients',
    path: '/patient-management',
    icon: <PatientsIcon />,
    match: 'exact',
    scope: StaffScope.PATIENTS_READ,
  },
  {
    id: 'staff',
    label: 'Staff',
    path: '/provider-management',
    icon: <StaffIcon />,
    match: 'exact',
    scope: StaffScope.STAFF_READ,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    path: '/marketing-management',
    icon: <CampaignIcon />,
    match: 'prefix',
    scope: StaffScope.MARKETING_READ,
  },
  {
    id: 'message',
    label: 'Messaging',
    path: '/message',
    icon: <CommunicationsIcon />,
    match: 'prefix',
    scope: StaffScope.MESSAGING_READ,
  },
  {
    id: 'insurance',
    label: 'Insurance',
    path: '/insurance',
    icon: <InsuranceIcon />,
    match: 'prefix',
  },
  {
    id: 'notes',
    label: 'Notes',
    path: '/notes',
    icon: <NotesIcon />,
    match: 'prefix',
    scope: StaffScope.NOTES_READ,
  },
  {
    id: 'files',
    label: 'Files',
    path: '/files',
    icon: <FilesIcon />,
    match: 'prefix',
    scope: StaffScope.FILES_READ,
  },
  {
    id: 'form-builder',
    label: 'Form builder',
    path: '/form-builder',
    icon: <FormBuilderIcon />,
    match: 'prefix',
    scope: StaffScope.FORMS_READ,
  },
  {
    id: 'charting',
    label: 'Charting',
    path: '/charting',
    icon: <ChartingIcon />,
    match: 'prefix',
    scope: StaffScope.CLINICAL_READ,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    path: '/tasks',
    icon: <NotesIcon />,
    match: 'exact',
    hidden: true,
  },
  {
    id: 'image-viewer',
    label: 'Image viewer',
    path: '/image-xrayviewer',
    icon: <ImageViewerIcon />,
    match: 'prefix',
    scope: StaffScope.IMAGES_READ,
  },
  {
    id: 'claims',
    label: 'Claims',
    path: '/claims',
    icon: <ClaimsIcon />,
    match: 'prefix',
    scope: StaffScope.CLAIMS_READ,
  },
  {
    id: 'billing',
    label: 'Billing',
    path: '/billing',
    icon: <BillingIcon />,
    match: 'prefix',
    scope: StaffScope.BILLING_READ,
  },
  {
    id: 'report',
    label: 'Reporting',
    path: '/report',
    icon: <ReportingIcon />,
    match: 'prefix',
    scope: StaffScope.REPORTING_READ,
  },
  {
    id: 'membership',
    label: 'Membership',
    path: '/membership',
    icon: <MembershipIcon />,
    match: 'prefix',
    scope: StaffScope.MEMBERSHIP_READ,
  },
]

const SETTINGS_ITEM: NavRouteItem = {
  id: 'settings',
  label: 'Settings',
  path: '/settings',
  icon: <SettingsIcon />,
  match: 'prefix',
  scope: StaffScope.SETTINGS_READ,
}

const isRouteActive = (item: NavRouteItem, pathname: string): boolean => {
  if (item.match === 'exact') {
    return pathname === item.path
  }

  if (item.path === '/') {
    return pathname === '/'
  }

  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}

const toDrawerItem = (item: NavRouteItem): DrawerItem => ({
  id: item.id,
  label: item.label,
  icon: item.icon,
})

const LeftSideBar: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const location = useLocation()
  const [expanded, setExpanded] = useState(false)

  const visibleRoutes = useMemo(
    () =>
      NAV_ROUTES.filter((item) => {
        if (item.hidden) return false
        if (!item.scope) return true
        return hasStaffScope(item.scope)
      }),
    [],
  )

  const items = useMemo(() => visibleRoutes.map(toDrawerItem), [visibleRoutes])

  const canAccessSettings = hasStaffScope(StaffScope.SETTINGS_READ)

  const footerItems = useMemo(
    () => (canAccessSettings ? [toDrawerItem(SETTINGS_ITEM)] : []),
    [canAccessSettings],
  )

  const activeId = useMemo(() => {
    const allRoutes = canAccessSettings
      ? [...visibleRoutes, SETTINGS_ITEM]
      : visibleRoutes

    // Prefer the most specific match (longest path) so nested routes win.
    const matches = allRoutes
      .filter((item) => isRouteActive(item, location.pathname))
      .sort((a, b) => b.path.length - a.path.length)

    return matches[0]?.id
  }, [location.pathname, visibleRoutes, canAccessSettings])

  const handleSelect = (id: string): void => {
    const route =
      visibleRoutes.find((item) => item.id === id) ??
      (canAccessSettings && id === SETTINGS_ITEM.id ? SETTINGS_ITEM : undefined)

    if (!route) return
    navigate(route.path)
  }

  return (
    <LeftSideBarWrapper>
      <Drawer
        items={items}
        footerItems={footerItems}
        activeId={activeId}
        onSelect={handleSelect}
        expanded={expanded}
        onExpandedChange={setExpanded}
        ariaLabel="Main navigation"
      />
    </LeftSideBarWrapper>
  )
}

export default LeftSideBar
